import { Component, ElementRef, HostListener, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { SearchService, SearchHit, SearchResults } from '../../services/search.service';

/**
 * The top-bar search: results as you type, grouped by entity, keyboard navigable.
 *
 * Replaces the previous behaviour (Enter → /projects?q=), which matched only
 * `projectName` against an already-loaded array.
 */
@Component({
  selector: 'app-global-search',
  imports: [CommonModule, FormsModule],
  templateUrl: './global-search.html',
})
export class GlobalSearchComponent {
  private searchService = inject(SearchService);
  private router = inject(Router);

  @ViewChild('input') inputRef?: ElementRef<HTMLInputElement>;

  query = '';
  open = signal(false);
  loading = signal(false);
  results = signal<SearchResults | null>(null);
  /** Index into `flatHits`, for arrow-key navigation. */
  active = signal(-1);

  private debounce?: ReturnType<typeof setTimeout>;
  /** Guards against an earlier request resolving after a later one. */
  private sequence = 0;

  get error(): string | null {
    return this.searchService.error;
  }

  /** All hits in display order, so the keyboard can walk across groups. */
  get flatHits(): SearchHit[] {
    const r = this.results();
    if (!r) return [];
    return [...r.projects, ...r.tasks, ...r.todos, ...r.clients];
  }

  readonly groups = [
    { key: 'projects', label: 'Projects' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'todos', label: 'To-dos' },
    { key: 'clients', label: 'Clients' },
  ] as const;

  hitsFor(key: 'projects' | 'tasks' | 'todos' | 'clients'): SearchHit[] {
    return this.results()?.[key] ?? [];
  }

  /** Position of a hit in the flat list, so highlighting survives grouping. */
  indexOf(hit: SearchHit): number {
    return this.flatHits.findIndex(h => h.objectID === hit.objectID && h.type === hit.type);
  }

  onInput() {
    this.open.set(true);
    this.active.set(-1);
    clearTimeout(this.debounce);

    const q = this.query.trim();
    if (!q) {
      this.results.set(null);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    // 180 ms is short enough to feel instant while collapsing a burst of keystrokes
    // into one request.
    this.debounce = setTimeout(() => this.run(q), 180);
  }

  private async run(q: string) {
    const mine = ++this.sequence;
    const results = await this.searchService.search(q);
    // Drop a response that a newer keystroke has already superseded.
    if (mine !== this.sequence) return;
    this.results.set(results);
    this.loading.set(false);
  }

  onKeydown(event: KeyboardEvent) {
    const hits = this.flatHits;

    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (!hits.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.active.set((this.active() + 1) % hits.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.active.set(this.active() <= 0 ? hits.length - 1 : this.active() - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.go(hits[this.active() >= 0 ? this.active() : 0]);
    }
  }

  go(hit: SearchHit) {
    this.close();
    this.query = '';
    this.results.set(null);
    this.router.navigate(hit.route, hit.queryParams ? { queryParams: hit.queryParams } : {});
  }

  close() {
    this.open.set(false);
    this.active.set(-1);
  }

  /** Close when focus leaves the whole control, not merely the input. */
  onFocusOut(event: FocusEvent) {
    const host = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (!next || !host.contains(next)) this.close();
  }

  /** Cmd/Ctrl-K focuses the box from anywhere. */
  @HostListener('document:keydown', ['$event'])
  onShortcut(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.inputRef?.nativeElement.focus();
      this.open.set(true);
    }
  }

  iconFor(type: SearchHit['type']): string {
    switch (type) {
      case 'project': return 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z';
      case 'task': return 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4';
      case 'todo': return 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'client': return 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4';
    }
  }
}
